import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { API } from '../constants';
import { PbDispatch } from './index';
import { pbRequestReset } from './pbRequestSlice';
import { PbRequest } from './pbRequest';

export interface VipUsersStore {
    addVipUsersReport: VipUserAddingReportDto | undefined;
    vipUsersInLaundryRoom: VipUsersInLaundryRoomDto | undefined;
    deleteVipUsersInLaundryRoomReport: VipUsersRemovalReportDto | undefined;
    locationVipPricing: LocationVipCostsDto | undefined;
}

interface UpdateVipCostsForLaundryRoomDto {
    cost_dryer_vip: number;
    cost_washing_machine_vip: number;
}

export interface VipUsersForRemovalRequest {
    vip_user_ids: number[];
}

export enum ServiceUserType {
    APP_USER = 'APP_USER',
    PHONE_USER = 'PHONE_USER',
    GUEST_STAY = 'GUEST_STAY',
    CITY_POP = 'CITY_POP',
    WHITE_LABEL = 'WHITE_LABEL',
}

export interface VipUser {
    user_id: number;
    customer_id: string;
    email: string;
    phone: string;
    service_user_type: ServiceUserType;
}

export interface VipUsersInLaundryRoomDto {
    location_id?: number;
    total_count: number;
    vip_users: VipUser[];
}

export interface VipUsersForAddingDto {
    customer_ids_or_emails: string[];
}

export interface VipUserAddingReportDto {
    location_id: number;
    reports: {
        email_or_customer_id: string;
        result: VipUserResult;
    }[];
}

export interface VipUsersRemovalReportDto {
    location_id: number;
    deleted_count: number;
    requested_count: number;
}

const initialState: VipUsersStore = {
    deleteVipUsersInLaundryRoomReport: undefined,
    vipUsersInLaundryRoom: undefined,
    addVipUsersReport: undefined,
    locationVipPricing: undefined,
};

const vipUsersSlice = createSlice({
    name: 'vipUsersStore',
    initialState,
    reducers: {
        setVipUsersInLaundryRoomRequest(
            state: VipUsersStore,
            action: PayloadAction<VipUsersInLaundryRoomDto>
        ) {
            state.vipUsersInLaundryRoom = action.payload;
        },
        setDeleteVipUsersInLaundryRoomReport(
            state: VipUsersStore,
            action: PayloadAction<VipUsersRemovalReportDto>
        ) {
            state.deleteVipUsersInLaundryRoomReport = action.payload;
        },
        setAddVipUsersReport: (
            state: VipUsersStore,
            action: PayloadAction<VipUserAddingReportDto | undefined>
        ) => {
            state.addVipUsersReport = action.payload;
        },
        setLocationVipPricing: (
            state: VipUsersStore,
            action: PayloadAction<LocationVipCostsDto>
        ) => {
            state.locationVipPricing = action.payload;
        },
    },
});

export function resetAddVipUsers() {
    return async (dispatch: PbDispatch) => {
        dispatch(vipUsersSlice.actions.setAddVipUsersReport(undefined));
        dispatch(pbRequestReset(PbRequest.POST_ADD_VIP_USERS));
    };
}

export interface VipUsersForAddingDto {
    customer_ids_or_emails: string[];
}

enum UserServiceType {
    APP_USER = 'APP_USER',
    PHONE_USER = 'PHONE_USER',
    GUEST_STAY = 'GUEST_STAY',
    CITY_POP = 'CITY_POP',
    WHITE_LABEL = 'WHITE_LABEL',
}

interface UserDetails {
    customer_id: string;
    email: string;
    serviceUserType: UserServiceType;
}

export interface LocationVipCostsDto {
    vip_costs_enabled: boolean;
    price_vip_dryer: number;
    price_normal_dryer: number;
    price_vip_washing_machine: number;
    price_normal_washing_machine: number;
    preview_vip_users: UserDetails[];
    laundry_room_name: string;
    total_vip_count: number;
    address: string;
}

export enum VipUserResult {
    SUCCESS = 'SUCCESS',
    UNKNOWN_USER = 'UNKNOWN_USER',
    ALREADY_EXISTING = 'ALREADY_EXISTING',
    OTHER_ERROR = 'OTHER_ERROR',
}

export interface VipUserReport {
    email_or_customer_id: string;
    result: VipUserResult;
}

export interface VipUserAddingReportDto {
    location_id: number;
    reports: VipUserReport[];
}

export function AddVipUsersToLaundryRoom(
    locationId: number,
    addVipUsers: VipUsersForAddingDto
) {
    return async (dispatch: PbDispatch) => {
        const request: RequestInit = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(addVipUsers),
        };
        const response =
            await processRequestForAccountingAreaWithReplacement<VipUserAddingReportDto>(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/vip_users`,
                request,
                PbRequest.POST_ADD_VIP_USERS
            );
        dispatch(vipUsersSlice.actions.setAddVipUsersReport(response));

        dispatch(GetVipUsersInLaundryRoom(locationId));
    };
}

export function GetVipUsersInLaundryRoom(locationId: number) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'GET',
            };
            const response =
                await processRequestForAccountingAreaWithReplacement<VipUsersInLaundryRoomDto>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/vip_users`,
                    request,
                    PbRequest.GET_VIP_USERS_IN_LAUNDRY_ROOM
                );
            dispatch(
                vipUsersSlice.actions.setVipUsersInLaundryRoomRequest(response)
            );
        } catch {
            // already handled
        }
    };
}

export function RemoveVipUsersFromLaundryRoom(
    locationId: number,
    vipUsersForRemoval: VipUsersForRemovalRequest
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(vipUsersForRemoval),
            };
            const response =
                await processRequestForAccountingAreaWithReplacement<VipUsersRemovalReportDto>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/remove/vip_users`,
                    request,
                    PbRequest.DELETE_VIP_USERS_IN_LAUNDRY_ROOM
                );
            dispatch(
                vipUsersSlice.actions.setDeleteVipUsersInLaundryRoomReport(
                    response
                )
            );
        } catch {
            // already handled
        }
    };
}

export function RequestLocationVipCosts(locationId: number) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = { method: 'GET' };

            const response =
                await processRequestForAccountingAreaWithReplacement<LocationVipCostsDto>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/vip_costs`,
                    request,
                    PbRequest.GET_LOCATION_VIP_PRICING
                );
            dispatch(vipUsersSlice.actions.setLocationVipPricing(response));
        } catch {
            //already handled
        }
    };
}

export function UpdateLocationVipCosts(
    locationId: number,
    price_vip_dryer: number,
    price_vip_washing_machine: number
) {
    return async (dispatch: PbDispatch) => {
        try {
            const requestBody: UpdateVipCostsForLaundryRoomDto = {
                cost_dryer_vip: price_vip_dryer,
                cost_washing_machine_vip: price_vip_washing_machine,
            };

            const request: RequestInit = {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            };

            await processRequestForAccountingAreaWithReplacement<UpdateVipCostsForLaundryRoomDto>(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/vip_costs`,
                request,
                PbRequest.POST_LOCATION_VIP_PRICING
            );
        } catch {
            //already handled
        }
    };
}

export default vipUsersSlice.reducer;
